import {
    ICreateOrderData,
    IAddItemBase,
    IAvailabilityDetailsToConfirm,
    IUpdateCartItemData,
    ICartItemData,
    IDirectPurchaseItemData,
    ICustomerNestedUpdate,
    IGuestNested,
    IGuestNestedUpdate,
} from "@interfaces";
import { AxiosResponse } from "axios";
import ftApiClient from "./ftApiClient";
import wtApiClient from "./wtApiClient";
import { normalizeItemsEnvelope } from "./normalizeItemsEnvelope";
import _ from "lodash";
import { IAPIResult } from "src/Interfaces/api";
import { buildCartRemoveUrl, RemovePayloadCart } from "@utilities";

export const cartApi = {
    addCart: (params: IAddItemBase, sessionId?: string) => {
        const url = sessionId ? `/cart/items/v2?sessionId=${sessionId}` : "/cart/items/v2";
        return ftApiClient.post(url, params);
    },
    addCartDryRun: (params: IAddItemBase) => {
        const url = "/cart/items/v2?dry=1";
        return ftApiClient.post<IAPIResult<ICartItemData[]>>(url, params);
    },
    getCart: async (
        sessionId?: string,
    ): Promise<AxiosResponse<IAPIResult<ICartItemData[]>>> => {
        // BREAKING: `data` is now `{ trip_start_date, trip_duration, items }`
        // (was a bare array). Unwrap so callers keep reading `data.data` as the
        // items array; legacy bare-array responses pass through unchanged.
        const url = sessionId ? `/cart/items?sessionId=${sessionId}` : "/cart/items";
        const res = await ftApiClient.get<IAPIResult<ICartItemData[]>>(url);
        return normalizeItemsEnvelope(res);
    },
    saveCart: (params: ICartItemData[]) => {
        const url = `cart/items/v2`;
        return ftApiClient.put(url, params);
    },
    updateCartItems: (items: IUpdateCartItemData[], sessionId?: string) => {
        const url = sessionId ? `cart/items?sessionId=${sessionId}` : `cart/items`;
        return ftApiClient.put(url, items);
    },
    updateCartItemsForCart: () => {
        const url = `cart/items/update?isCart=1`;
        return ftApiClient.put(url);
    },
    removeCartItem: (payload: RemovePayloadCart, sessionId?: string) => {
        const url = buildCartRemoveUrl(payload, sessionId);
        return ftApiClient.delete(url);
    },
    removeCartItemById: (cartItemId: string | number, sessionId?: string) => {
        const params = new URLSearchParams();
        params.set("cartItemId", cartItemId.toString());
        if (sessionId) params.set("sessionId", sessionId);
        return ftApiClient.delete(`cart/remove/items?${params.toString()}`);
    },
    createOrder: (data: ICreateOrderData, sessionId?: string) => {
        const url = sessionId ? `cart/order?sessionId=${sessionId}` : "cart/order";
        return ftApiClient.post(url, data);
    },
    completeOrder: (bookingReference: string) => {
        const url = `/cart/order/complete`;
        return ftApiClient.put(url, {}, { params: { bookingReference } });
    },
    confirmAvailability: (
        productPriceDetailsId: number | string,
        bookingDate: string | Date,
        timeId?: string,
    ) => {
        const url = _.isNil(timeId)
            ? `bookingdetails/detailsToConfirmAvailability/${productPriceDetailsId}/${bookingDate}`
            : `bookingdetails/detailsToConfirmAvailability/${productPriceDetailsId}/${bookingDate}/${timeId}`;
        return wtApiClient.get<IAvailabilityDetailsToConfirm>(url);
    },
    purchaseDirect: (
        data: IDirectPurchaseItemData & { pointsApplied: number },
        sessionId?: string,
    ) => {
        const url = sessionId
            ? `cart/order/v2/direct-purchase/v2?sessionId=${sessionId}`
            : `cart/order/v2/direct-purchase/v2`;
        return ftApiClient.post(url, data);
    },
    mergeSessionCart: (sessionId: string, accessToken?: string) => {
        const url = `/cart/convert/session/${sessionId}`;
        const config = accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined;
        return ftApiClient.put(url, undefined, config);
    },
    updateCustomer: (data: ICustomerNestedUpdate, sessionId?: string) => {
        const url = sessionId ? `cart/customers?sessionId=${sessionId}` : "cart/customers";
        return ftApiClient.put(url, data);
    },
    updateGuest: (data: IGuestNestedUpdate, sessionId?: string) => {
        const url = sessionId ? `cart/guests?sessionId=${sessionId}` : "cart/guests";
        return ftApiClient.put(url, data);
    },
    // ---- Account-level Address Book (MongoDB-backed) ----
    // Logged-in user's profile + saved travellers ("guests").
    getCustomers: () => {
        return ftApiClient.get("cart/customers");
    },
    // Create OR update a single saved traveller. Include `_id` to update an
    // existing one; omit it to create a new one.
    saveGuest: (data: Partial<IGuestNested>) => {
        return ftApiClient.post("cart/guests", data);
    },
    // Remove one saved traveller by its Mongo id (`_id.$oid`).
    deleteGuest: (guestId: string) => {
        return ftApiClient.delete(`cart/guests/${guestId}`);
    },
};
